import {
  collection,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';

const DEFAULT_ORGANIZATION_ID =
  process.env.REACT_APP_DEFAULT_ORGANIZATION_ID || 'default_organization';

export const getTelephonyOrganizationId = (user) =>
  user?.organizationId || DEFAULT_ORGANIZATION_ID;

export const canManageTelephony = (user) => (
  user?.isAdmin === true ||
  ['admin', 'super_admin', 'central_admin'].includes(user?.role)
);

export const canReceiveTelephonyCalls = (user) => (
  user?.isAdmin === true ||
  [
    'admin',
    'super_admin',
    'central_admin',
    'dispatcher',
    'central_dispatch',
    'operator_admin',
    'operator_staff'
  ].includes(user?.role)
);

const telephonyScopeConstraints = (user) => {
  const constraints = [
    where('organizationId', '==', getTelephonyOrganizationId(user))
  ];
  if (['operator_admin', 'operator_staff'].includes(user?.role) && user?.operatorId) {
    constraints.push(where('operatorId', '==', user.operatorId));
  }
  return constraints;
};

export const createTelephonyPairingCode = async (displayName) => {
  const callable = httpsCallable(functions, 'createTelephonyPairingCode');
  const result = await callable({ displayName: displayName?.trim() || '' });
  return result.data;
};

export const disableTelephonyConnector = async (connectorId) => {
  const callable = httpsCallable(functions, 'disableTelephonyConnector');
  const result = await callable({ connectorId });
  return result.data;
};

export const deleteTelephonyConnector = async (connectorId) => {
  const callable = httpsCallable(functions, 'deleteTelephonyConnector');
  const result = await callable({ connectorId });
  return result.data;
};

export const setTelephonyConnectorRole = async (connectorId, role) => {
  const callable = httpsCallable(functions, 'setTelephonyConnectorRole');
  const result = await callable({ connectorId, role });
  return result.data;
};

export const subscribeToTelephonyConnectors = (user, onData, onError) => {
  if (!user || !canReceiveTelephonyCalls(user)) return () => {};

  const connectorsQuery = query(
    collection(db, 'telephonyConnectors'),
    ...telephonyScopeConstraints(user)
  );

  return onSnapshot(connectorsQuery, (snapshot) => {
    const items = snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
    onData(items.slice(0, 20));
  }, onError);
};

export const subscribeToIncomingCalls = (user, onData, onError) => {
  if (!user || !canReceiveTelephonyCalls(user)) return () => {};

  const callsQuery = query(
    collection(db, 'incomingCallSessions'),
    ...telephonyScopeConstraints(user)
  );

  return onSnapshot(callsQuery, (snapshot) => {
    const items = snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .sort((a, b) => (toDate(b.startedAt)?.getTime() || 0) - (toDate(a.startedAt)?.getTime() || 0));
    onData(items.slice(0, 20));
  }, onError);
};

export const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
